import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Loader2, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { buildReportPages, renderPagesToPDF } from '../utils/reportPdf';

// Live, in-app preview of the exact dark-canvas PDF pages, with download.
export function ReportPreviewModal({ open, onClose, data, sectionKeys, onDownloaded }) {
  const stageRef = useRef(null);
  const shellRef = useRef(null);
  const pagesRef = useRef([]);
  const nameRef = useRef('Student');
  const [building, setBuilding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [scale, setScale] = useState(1);

  const computeScale = useCallback(() => {
    const avail = (shellRef.current?.clientWidth || window.innerWidth) - 48;
    setScale(Math.max(0.32, Math.min(1, avail / 816)));
  }, []);

  useEffect(() => {
    if (!open) return;
    computeScale();
    window.addEventListener('resize', computeScale);
    return () => window.removeEventListener('resize', computeScale);
  }, [open, computeScale]);

  useEffect(() => {
    if (!open || !data) return;
    let cancelled = false;
    setBuilding(true);
    setPageCount(0);
    (async () => {
      try {
        const { pages, name } = await buildReportPages(data, sectionKeys || []);
        if (cancelled) return;
        pagesRef.current = pages;
        nameRef.current = name;
        const stage = stageRef.current;
        if (stage) {
          stage.innerHTML = '';
          pages.forEach((p) => {
            const frame = document.createElement('div');
            frame.style.cssText =
              'width:816px;margin:0 auto 22px auto;box-shadow:0 18px 50px rgba(0,0,0,0.55);border-radius:16px;overflow:hidden;';
            frame.appendChild(p);
            stage.appendChild(frame);
          });
        }
        setPageCount(pages.length);
      } finally {
        if (!cancelled) setBuilding(false);
      }
    })();
    return () => {
      cancelled = true;
      if (stageRef.current) stageRef.current.innerHTML = '';
      pagesRef.current = [];
    };
  }, [open, data, sectionKeys]);

  const handleDownload = async () => {
    if (!pagesRef.current.length) return;
    setDownloading(true);
    try {
      await renderPagesToPDF(pagesRef.current, nameRef.current);
      onDownloaded?.();
    } finally {
      setDownloading(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex flex-col bg-[#01040E]/92 backdrop-blur-sm"
      data-testid="report-preview-modal"
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-blue-500/20 bg-[#04101F]/90">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm truncate">Report Preview</p>
            <p className="text-slate-300 text-xs">
              {building ? 'Building your report…' : `${pageCount} page${pageCount === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleDownload}
            disabled={building || downloading || !pageCount}
            className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white"
            data-testid="preview-download-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download PDF
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-slate-300 hover:text-white hover:bg-slate-800/60"
            data-testid="preview-close-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Scroll area */}
      <div ref={shellRef} className="flex-1 overflow-auto py-8 px-4">
        {building && (
          <div className="flex flex-col items-center justify-center h-full text-slate-300 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            <p className="text-sm">Assembling your premium report…</p>
          </div>
        )}
        <div style={{ zoom: scale, width: 816, margin: '0 auto' }}>
          <div ref={stageRef} />
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ReportPreviewModal;
